import type { LatLonPoint } from './barriers';

export type RouteSource = 'gis_built' | 'imported' | 'manual';

/**
 * Enumeration of route variant keys.
 *
 * - `gis`: raw provider response (OSRM at the moment).
 * - `iteko`: reserved for the future external ITEKO provider.
 * - `barrier_aware`: `gis` re-routed through injected barrier waypoints.
 */
export type RouteVariantKey = 'gis' | 'iteko' | 'barrier_aware';

export const ROUTE_VARIANT_KEYS: readonly RouteVariantKey[] = [
  'gis',
  'iteko',
  'barrier_aware',
] as const;

export interface VariantSummary {
  readonly variant: RouteVariantKey;
  readonly provider: string | null;
  readonly length_km: number;
  readonly duration_min: number | null;
  readonly applied_barriers: readonly number[];
  readonly residual_barriers: readonly number[];
  readonly stale: boolean;
}

export interface RouteRequest {
  readonly waypoints: readonly LatLonPoint[];
  readonly from_address: string | null;
  readonly to_address: string | null;
}

/**
 * OD-level route entity returned by `GET /routes` and friends.
 *
 * Per-variant metrics live inside `variants[]`. The list may contain
 * 1..3 entries. `gis` is always present; `barrier_aware` only when the
 * raw GIS route crossed at least one barrier; `iteko` stays reserved
 * for the future external provider.
 */
export interface RouteResponse {
  readonly route_id: string;
  readonly source: RouteSource;
  readonly waypoints: readonly LatLonPoint[];
  readonly from_address: string | null;
  readonly to_address: string | null;
  readonly created_at: string;
  readonly updated_at: string;
  readonly variants: readonly VariantSummary[];
}

export interface RouteListFilters {
  readonly periodFrom: string | null;
  readonly periodTo: string | null;
  readonly lengthMinKm: number | null;
  readonly lengthMaxKm: number | null;
  readonly hasAppliedBarriers: boolean | null;
  readonly hasResidualBarriers: boolean | null;
  readonly search: string;
}

/** Server-side sort field for the routes library list. */
export type RouteSortField = 'created_at' | 'updated_at' | 'length_km';

export type RouteSortDirection = 'asc' | 'desc';

export interface RouteListSort {
  readonly field: RouteSortField;
  readonly direction: RouteSortDirection;
}

export interface RoutesListResponse {
  readonly items: readonly RouteResponse[];
  readonly total: number;
}

export interface RouteIdsResponse {
  readonly route_ids: readonly string[];
  readonly total: number;
}

export interface BulkDeleteResponse {
  readonly deleted: number;
}

export interface BulkRecomputeResponse {
  readonly marked_stale: number;
}

export interface RouteGeoJsonFeature {
  readonly type: 'Feature';
  readonly id: string;
  readonly geometry: {
    readonly type: 'LineString';
    readonly coordinates: ReadonlyArray<readonly [number, number]>;
  };
  readonly properties: {
    readonly variant: RouteVariantKey;
    readonly length_km: number;
    readonly duration_min: number | null;
    readonly applied_barriers: readonly number[];
    readonly residual_barriers: readonly number[];
    readonly stale: boolean;
    readonly created_at: string;
  };
}

export interface BatchGeometryFeature {
  readonly type: 'Feature';
  readonly id: string;
  readonly geometry: {
    readonly type: 'LineString';
    readonly coordinates: ReadonlyArray<readonly [number, number]>;
  };
  readonly properties: {
    readonly route_id: string;
    readonly variant: RouteVariantKey;
    readonly length_km: number;
    readonly duration_min: number | null;
    readonly applied_barriers: readonly number[];
    readonly residual_barriers: readonly number[];
    readonly stale: boolean;
  };
}

export interface VariantFeatureCollection {
  readonly variant: RouteVariantKey;
  readonly type: 'FeatureCollection';
  readonly features: readonly BatchGeometryFeature[];
}

export interface GeometryBatchResponse {
  readonly collections: readonly VariantFeatureCollection[];
}

/** Returns the variant used by Data1861 consumers (barrier_aware > gis). */
export function pickCanonicalVariant(
  variants: readonly VariantSummary[],
): VariantSummary | null {
  const barrierAware = variants.find((v) => v.variant === 'barrier_aware');
  if (barrierAware) return barrierAware;
  return variants.find((v) => v.variant === 'gis') ?? null;
}
